/**
 * Edit Project Modal - Update EAS project details including access token
 */

import { useState } from "react"
import toast from "react-hot-toast"

import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"

interface EditProjectModalProps {
  project: {
    id: string
    name: string
    slug: string
    description?: string
    icon?: string
    accessToken?: string
  }
  onClose: () => void
  onSuccess: () => void
}

export function EditProjectModal({ project, onClose, onSuccess }: EditProjectModalProps) {
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description || "",
    icon: project.icon || "",
    accessToken: "", // Don't pre-fill for security (user must paste new token)
  })

  const updateProject = trpc.easUpdates.updateProject.useMutation({
    onSuccess: () => {
      toast.success("Project updated successfully!")
      onSuccess()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Build update payload (only send fields that changed)
    const updates: any = {
      id: project.id,
    }

    if (formData.name !== project.name) {
      updates.name = formData.name
    }

    if (formData.description !== (project.description || "")) {
      updates.description = formData.description
    }

    if (formData.icon !== (project.icon || "")) {
      updates.icon = formData.icon
    }

    if (formData.accessToken.trim()) {
      updates.accessToken = formData.accessToken.trim()
    }

    // Check if anything changed
    if (Object.keys(updates).length === 1) {
      toast.error("No changes to save")
      return
    }

    updateProject.mutate(updates)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-foreground mb-4">Edit Project</h2>

        <form onSubmit={handleSubmit}>
          {/* Project Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Project Name</label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Pebble"
              className="w-full"
            />
          </div>

          {/* Icon */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Icon (Emoji)</label>
            <Input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="📱"
              className="w-full"
              maxLength={2}
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="AI-powered productivity app for iOS"
              className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground"
              rows={3}
            />
          </div>

          {/* EAS Access Token */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">Update EAS Access Token (optional)</label>
            <Input
              type="password"
              value={formData.accessToken}
              onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
              placeholder="Leave empty to keep current token, or paste new token"
              className="w-full font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Get a new token from:{" "}
              <a
                href="https://expo.dev/accounts/[account]/settings/access-tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Expo Dashboard → Access Tokens
              </a>
              <br />
              <span className="text-yellow-600">
                ⚠️ For Robot tokens, make sure to add the robot to your project first!
              </span>
            </p>
          </div>

          {/* Readonly Fields Info */}
          <div className="mb-6 p-3 bg-secondary rounded-md">
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> Slug, App ID, platforms, and channels cannot be edited. If you need to change
              these, please create a new project.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button type="button" onClick={onClose} className="bg-secondary">
              Cancel
            </Button>
            <Button type="submit" loading={updateProject.isLoading}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
